import React, { useState, useEffect, useRef } from 'react';
import { useDataStore } from '../store/useDataStore';
import { Plus, Clock, Trash2, Layout as LayoutIcon, Upload, Download, Loader2, RefreshCw } from 'lucide-react';

interface WorksViewProps {
  onOpenProject: (projectId: string) => void;
}

export const WorksView: React.FC<WorksViewProps> = ({ onOpenProject }) => {
  const { data, addProject, deleteProject, pushAllProjectsToServer, pullAllProjectsFromServer, userUuid } = useDataStore();
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRegeneratingCovers, setIsRegeneratingCovers] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  const [coverImageUrls, setCoverImageUrls] = useState<Record<string, string>>({});

  // 用于强制刷新封面的计数器
  const [refreshCounter, setRefreshCounter] = useState(0)
  
  // 记录上一次的 userUuid，用于检测用户切换
  const [prevUserUuid, setPrevUserUuid] = useState<string | null>(null)

  // 监听 userUuid 变化，切换用户时强制重新加载
  useEffect(() => {
    if (prevUserUuid !== null && prevUserUuid !== userUuid) {
      // 用户切换了，清空封面图URL并增加刷新计数器
      console.log('[WorksView] userUuid 已变化:', prevUserUuid, '->', userUuid, '项目数量:', data.projects.length)
      setCoverImageUrls({})
      setRefreshCounter(prev => prev + 1)
    }
    setPrevUserUuid(userUuid)
  }, [userUuid, prevUserUuid, data.projects.length])

  // 监听用户切换事件，确保切换用户时强制刷新
  useEffect(() => {
    const handleUserSwitched = (event: CustomEvent) => {
      console.log('[WorksView] 收到用户切换事件:', event.detail)
      // 清空封面图URL并增加刷新计数器，强制重新加载
      setCoverImageUrls({})
      setRefreshCounter(prev => prev + 1)
    }
    
    // 监听封面更新事件
    const handleCoverUpdated = (event: CustomEvent) => {
      console.log('[WorksView] 收到封面更新事件:', event.detail)
      // 增加刷新计数器，强制重新加载封面
      setRefreshCounter(prev => prev + 1)
    }
    
    window.addEventListener('userSwitched', handleUserSwitched as EventListener)
    window.addEventListener('coverUpdated', handleCoverUpdated as EventListener)
    
    return () => {
      window.removeEventListener('userSwitched', handleUserSwitched as EventListener)
      window.removeEventListener('coverUpdated', handleCoverUpdated as EventListener)
    }
  }, [])

  // 计算项目的 uuid 列表的签名，用于检测项目列表变化
  const projectsSignature = data.projects.map(p => p.uuid).sort().join(',')

  // 从用户数据库 IN_{uuid} 加载封面图 URL
  useEffect(() => {
    // 确保有用户 UUID
    if (!userUuid) {
      setCoverImageUrls({});
      return;
    }

    const loadCoverImages = async () => {
      let db: IDBDatabase | null = null;
      
      try {
        // 打开用户数据库 IN_{uuid}
        const dbName = `IN_${userUuid}`;
        db = await new Promise<IDBDatabase>((resolve, reject) => {
          const request = indexedDB.open(dbName);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        // 检查数据库是否有 projects 表
        if (!db.objectStoreNames.contains('projects')) {
          console.log('[WorksView] 用户数据库没有 projects 表');
          setCoverImageUrls({});
          return;
        }

        // 从 projects 表加载所有项目（包含封面信息）
        const projectsStore = db.transaction(['projects'], 'readonly').objectStore('projects');
        const allProjects = await new Promise<any[]>((resolve, reject) => {
          const request = projectsStore.getAll();
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        });

        console.log(`[WorksView] 从用户数据库加载了 ${allProjects.length} 个项目的封面信息`);

        const urls: Record<string, string> = {};

        for (const project of allProjects) {
          // 优先使用缩略图，其次使用封面图
          const fileIdToLoad = project.coverThumbnailFileId || project.coverImage;
          
          // 使用 id 作为 key（数据库主键是 id）
          const projectKey = project.id;
          if (!projectKey) continue;
          
          if (!fileIdToLoad) continue;

          // 如果是 base64 格式（向后兼容），直接使用
          if (fileIdToLoad.startsWith('data:image/')) {
            urls[projectKey] = fileIdToLoad;
            continue;
          }

          try {
            // 从 media 表加载
            if (db.objectStoreNames.contains('media')) {
              const mediaStore = db.transaction(['media'], 'readonly').objectStore('media');
              
              // 如果 fileId 包含扩展名，提取原始 UUID
              let originalFileId = fileIdToLoad;
              if (fileIdToLoad.includes('.')) {
                originalFileId = fileIdToLoad.substring(0, fileIdToLoad.lastIndexOf('.'));
              }

              const result = await new Promise<any>((resolve, reject) => {
                const request = mediaStore.get(originalFileId);
                request.onsuccess = () => resolve(request.result);
                request.onerror = () => reject(request.error);
              });

              if (result && result.blob) {
                urls[projectKey] = URL.createObjectURL(result.blob);
              }
            }
          } catch (error) {
            console.error('加载封面图失败:', project.name, fileIdToLoad, error);
          }
        }

        console.log(`[WorksView] 成功加载 ${Object.keys(urls).length} 个封面图`);
        setCoverImageUrls(urls);
      } catch (error) {
        console.error('[WorksView] 加载封面图失败:', error);
        // 延迟重试
        setTimeout(() => {
          loadCoverImages();
        }, 500);
      } finally {
        if (db) {
          db.close();
        }
      }
    };

    loadCoverImages();
  }, [userUuid, refreshCounter, projectsSignature]);
  
  const handleCreateNew = () => {
    const projectId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    const newProject = {
      id: projectId,  // IndexedDB keyPath
      uuid: crypto.randomUUID(),
      name: '新画布',
      groups: [],
      items: [],
      canvasOffset: { x: 0, y: 0 },
      canvasScale: 1,
      opened: true, // 新项目默认打开
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    addProject(newProject as any);
    onOpenProject(projectId);  // 使用 id 而不是 uuid
  };

  // 显示 toast 提示（使用原生代码的 showToast 函数）
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const win = window as any
    if (typeof win.showToast === 'function') {
      win.showToast(message, type)
    } else {
      console.log(`[Toast ${type}] ${message}`)
    }
  }

  // 懒加载封面图组件
  const LazyCoverImage: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const imgRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        },
        { rootMargin: '100px', threshold: 0.1 }
      )

      if (imgRef.current) {
        observer.observe(imgRef.current)
      }

      return () => observer.disconnect()
    }, [])

    if (hasError) {
      return (
        <LayoutIcon className="text-pink-200 group-hover:text-pink-300 transition-all duration-500 scale-125 group-hover:scale-150 group-hover:rotate-12" size={48} />
      )
    }

    return (
      <div ref={imgRef} className="w-full h-full relative">
        {!isLoaded && (
          <div className="absolute inset-0 bg-pink-50 animate-pulse" />
        )}
        {isVisible && (
          <img 
            src={src} 
            alt={alt} 
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
          />
        )}
      </div>
    )
  }

  const handleUploadAll = async () => {
    if (!userUuid) {
      showToast('请先登录', 'error');
      return;
    }
    if (data.projects.length === 0) {
      showToast('没有可上传的项目', 'info');
      return;
    }
    
    setIsUploading(true);
    setProgress(null);
    
    try {
      const result = await pushAllProjectsToServer((current, total, name) => {
        setProgress({ current, total, name });
      });
      
      if (result.failed > 0) {
        showToast(`上传完成！成功 ${result.success} 个，失败 ${result.failed} 个`, 'info');
      } else {
        showToast(`全部上传成功！共 ${result.success} 个项目`, 'success');
      }
    } catch (error) {
      console.error('批量上传失败:', error);
      showToast('上传失败: ' + (error as Error).message, 'error');
    } finally {
      setIsUploading(false);
      setProgress(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!userUuid) {
      showToast('请先登录', 'error');
      return;
    }
    if (data.projects.length === 0) {
      showToast('没有可下载的项目', 'info');
      return;
    }
    
    setIsDownloading(true);
    setProgress(null);
    
    try {
      const result = await pullAllProjectsFromServer((current, total, name) => {
        setProgress({ current, total, name });
      });
      
      // 下载完成后，强制刷新封面图（因为媒体文件是在项目数据更新后才下载的）
      setCoverImageUrls({});
      setRefreshCounter(prev => prev + 1);
      
      if (result.failed > 0) {
        showToast(`下载完成！成功 ${result.success} 个，失败 ${result.failed} 个`, 'info');
      } else {
        showToast(`全部下载成功！共 ${result.success} 个项目`, 'success');
      }
    } catch (error) {
      console.error('批量下载失败:', error);
      showToast('下载失败: ' + (error as Error).message, 'error');
    } finally {
      setIsDownloading(false);
      setProgress(null);
    }
  };

  const handleRegenerateAllCovers = async () => {
    const win = window as any;
    
    // 使用 React store 的项目数据（现在已经从 IndexedDB 加载，包含完整的 items）
    const projectsToProcess = data.projects;
    
    if (projectsToProcess.length === 0) {
      showToast('没有可生成封面的项目', 'info');
      return;
    }

    // 打印调试信息
    console.log('📋 项目列表:', projectsToProcess.map((p: any) => ({
      name: p.name,
      uuid: p.uuid,
      id: p.id,
      itemsCount: p.items?.length || 0
    })));

    if (!confirm(`确定要重新生成所有 ${projectsToProcess.length} 个项目的封面图吗？\n\n注意：只有包含内容的项目才会生成封面。`)) {
      return;
    }

    setIsRegeneratingCovers(true);
    setProgress({ current: 0, total: projectsToProcess.length, name: '准备中...' });

    try {
      // 检查 generateProjectCover 函数是否可用
      if (typeof win.generateProjectCover !== 'function') {
        throw new Error('生成封面功能不可用，请刷新页面后重试');
      }

      const { updateProject } = useDataStore.getState();
      let successCount = 0;
      let failCount = 0;
      
      // 收集需要更新的封面数据
      const coverUpdates: { projectId: string; coverImage: string; coverThumbnailFileId?: string }[] = [];

      // 遍历所有项目
      for (let i = 0; i < projectsToProcess.length; i++) {
        const project = projectsToProcess[i];
        
        setProgress({ 
          current: i + 1, 
          total: projectsToProcess.length, 
          name: project.name || `项目 ${i + 1}` 
        });

        try {
          // 检查项目是否有内容
          if (!project.items || project.items.length === 0) {
            console.log(`⏭️ 跳过项目 "${project.name}"：没有内容`);
            continue;
          }

          console.log(`🎨 开始生成项目 "${project.name}" 的封面 (items: ${project.items.length})`);

          // 直接使用项目数据生成封面
          const coverResult = await win.generateProjectCover(project);
          
          if (coverResult && coverResult.fileId) {
            console.log(`✅ 项目 "${project.name}" 封面生成成功:`, coverResult);
            
            const projectId = project.id || project.uuid;
            
            // 1. 更新 React store
            updateProject(projectId, {
              coverImage: coverResult.fileId,
              coverThumbnailFileId: coverResult.thumbnailFileId || undefined,
              updatedAt: Date.now()
            });
            
            // 2. 收集封面更新数据
            coverUpdates.push({
              projectId,
              coverImage: coverResult.fileId,
              coverThumbnailFileId: coverResult.thumbnailFileId
            });

            successCount++;
          } else {
            console.warn(`⚠️ 项目 "${project.name}" 封面生成失败：返回结果为空`);
            failCount++;
          }
        } catch (error) {
          console.error(`❌ 生成项目 "${project.name}" 封面失败:`, error);
          failCount++;
        }
      }

      // 批量更新用户数据库 IN_{uuid} 中的项目封面
      if (coverUpdates.length > 0 && userUuid) {
        try {
          const dbName = `IN_${userUuid}`;
          const db = await new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(dbName);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
          });

          if (db.objectStoreNames.contains('projects')) {
            const transaction = db.transaction(['projects'], 'readwrite');
            const store = transaction.objectStore('projects');
            
            // 获取所有项目
            const allProjects = await new Promise<any[]>((resolve, reject) => {
              const request = store.getAll();
              request.onsuccess = () => resolve(request.result || []);
              request.onerror = () => reject(request.error);
            });

            // 更新每个项目的封面
            for (const update of coverUpdates) {
              const project = allProjects.find(p => p.id === update.projectId || p.uuid === update.projectId);
              if (project) {
                project.coverImage = update.coverImage;
                if (update.coverThumbnailFileId) {
                  project.coverThumbnailFileId = update.coverThumbnailFileId;
                }
                project.updatedAt = new Date().toISOString();
                
                await new Promise<void>((resolve, reject) => {
                  const putRequest = store.put(project);
                  putRequest.onsuccess = () => resolve();
                  putRequest.onerror = () => reject(putRequest.error);
                });
                
                console.log(`💾 已更新用户数据库中项目 "${project.name}" 的封面`);
              }
            }
            
            console.log(`✅ 已批量更新 ${coverUpdates.length} 个项目的封面到用户数据库`);
          }
          
          db.close();
        } catch (error) {
          console.error('批量更新用户数据库中项目封面失败:', error);
        }
      }

      // 直接使用更新后的封面数据刷新显示
      const newCoverUrls: Record<string, string> = { ...coverImageUrls };
      
      for (const update of coverUpdates) {
        const project = projectsToProcess.find(p => p.id === update.projectId || p.uuid === update.projectId);
        if (!project) continue;
        
        const fileIdToLoad = update.coverThumbnailFileId || update.coverImage;
        
        // 如果是 base64，直接使用
        if (fileIdToLoad.startsWith('data:image/')) {
          newCoverUrls[project.uuid] = fileIdToLoad;
          continue;
        }
        
        // 从 IndexedDB 加载封面图
        try {
          if (typeof win.loadMediaFromIndexedDB === 'function') {
            const mediaData = await win.loadMediaFromIndexedDB(fileIdToLoad);
            if (mediaData && mediaData.url) {
              newCoverUrls[project.uuid] = mediaData.url;
              continue;
            }
          }
          
          if (typeof win.openDB === 'function') {
            const db = await win.openDB();
            const STORE_MEDIA = 'media';
            const mediaStore = db.transaction([STORE_MEDIA], 'readonly').objectStore(STORE_MEDIA);
            
            let originalFileId = fileIdToLoad;
            if (fileIdToLoad && fileIdToLoad.includes('.')) {
              originalFileId = fileIdToLoad.substring(0, fileIdToLoad.lastIndexOf('.'));
            }
            
            const result = await new Promise((resolve, reject) => {
              const request = mediaStore.get(originalFileId);
              request.onsuccess = () => resolve(request.result);
              request.onerror = () => reject(request.error);
            });
            
            if (result && (result as any).blob) {
              newCoverUrls[project.uuid] = URL.createObjectURL((result as any).blob);
            }
          }
        } catch (error) {
          console.error('加载封面图失败:', project.name, fileIdToLoad, error);
        }
      }
      
      setCoverImageUrls(newCoverUrls);

      // 显示结果
      if (failCount > 0) {
        showToast(`封面生成完成！成功 ${successCount} 个，失败 ${failCount} 个`, 'info');
      } else {
        showToast(`全部封面生成成功！共 ${successCount} 个项目`, 'success');
      }
    } catch (error) {
      console.error('批量生成封面失败:', error);
      showToast('生成封面失败: ' + (error as Error).message, 'error');
    } finally {
      setIsRegeneratingCovers(false);
      setProgress(null);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto h-full overflow-y-auto custom-scrollbar">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-800 mb-2 tracking-tight">我的作品</h1>
          <p className="text-gray-400 font-medium">🎨 记录每一个灵光一现的瞬间</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* 重新生成封面按钮 */}
          <button 
            onClick={handleRegenerateAllCovers}
            disabled={isRegeneratingCovers || isUploading || isDownloading || data.projects.length === 0}
            className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white px-5 py-3.5 rounded-2xl font-bold shadow-[0_8px_16px_rgba(147,51,234,0.2)] transition-all flex items-center gap-2 active:scale-95 disabled:cursor-not-allowed"
            title="重新生成所有项目的封面图"
          >
            {isRegeneratingCovers ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <RefreshCw size={20} />
            )}
            <span className="hidden sm:inline">重新生成封面</span>
          </button>
          
          {/* 全部上传按钮 */}
          <button 
            onClick={handleUploadAll}
            disabled={isUploading || isDownloading || isRegeneratingCovers || data.projects.length === 0}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-5 py-3.5 rounded-2xl font-bold shadow-[0_8px_16px_rgba(59,130,246,0.2)] transition-all flex items-center gap-2 active:scale-95 disabled:cursor-not-allowed"
            title="上传所有项目到云端"
          >
            {isUploading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Upload size={20} />
            )}
            <span className="hidden sm:inline">全部上传</span>
          </button>
          
          {/* 全部下载按钮 */}
          <button 
            onClick={handleDownloadAll}
            disabled={isUploading || isDownloading || isRegeneratingCovers || data.projects.length === 0}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-5 py-3.5 rounded-2xl font-bold shadow-[0_8px_16px_rgba(34,197,94,0.2)] transition-all flex items-center gap-2 active:scale-95 disabled:cursor-not-allowed"
            title="从云端下载更新所有项目"
          >
            {isDownloading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Download size={20} />
            )}
            <span className="hidden sm:inline">全部下载</span>
          </button>
          
          {/* 新建画布按钮 */}
          <button 
            onClick={handleCreateNew}
            className="bg-[#ff5c5c] hover:bg-[#ff4444] text-white px-8 py-3.5 rounded-2xl font-bold shadow-[0_10px_20px_rgba(255,92,92,0.2)] transition-all flex items-center gap-2 active:scale-95 group"
          >
            <Plus size={22} strokeWidth={3} className="group-hover:rotate-90 transition-transform" />
            <span>新建画布</span>
          </button>
        </div>
      </div>

      {/* 进度提示 */}
      {progress && (
        <div className="mb-6 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <Loader2 size={20} className="animate-spin text-blue-500" />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                {isUploading ? '正在上传' : isDownloading ? '正在下载' : isRegeneratingCovers ? '正在生成封面' : '处理中'}: {progress.name}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                进度: {progress.current} / {progress.total}
              </div>
            </div>
            <div className="text-sm font-bold text-blue-500">
              {Math.round((progress.current / progress.total) * 100)}%
            </div>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {data.projects.map((project, index) => (
          <div 
            key={project.uuid}
            onClick={() => onOpenProject(project.uuid)}
            className="bg-white rounded-[32px] p-2 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-pink-50 hover:shadow-[0_20px_50px_rgba(255,182,193,0.15)] transition-all cursor-pointer group relative flex flex-col h-[280px]"
            style={{ 
              transform: `rotate(${index % 2 === 0 ? 0.5 : -0.5}deg)`,
            }}
          >
            <div className="flex-1 bg-pink-50/50 rounded-[24px] mb-2 flex items-center justify-center group-hover:bg-pink-100/50 transition-colors overflow-hidden relative">
              {(project.id && coverImageUrls[project.id]) || (project.coverImage && project.coverImage.startsWith('data:image/') ? project.coverImage : null) ? (
                <LazyCoverImage 
                  src={(project.id && coverImageUrls[project.id]) || project.coverImage} 
                  alt={project.name} 
                />
              ) : (
                <LayoutIcon className="text-pink-200 group-hover:text-pink-300 transition-all duration-500 scale-125 group-hover:scale-150 group-hover:rotate-12" size={48} />
              )}
              
              {/* Card Decoration */}
              {!(project.id && coverImageUrls[project.id]) && !(project.coverImage && project.coverImage.startsWith('data:image/')) && (
                <>
                  <div className="absolute top-4 left-4 w-2 h-2 rounded-full bg-pink-200 opacity-50" />
                  <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-blue-200 opacity-50" />
                </>
              )}
            </div>
            
            <div className="px-4 pb-4">
              <h3 className="font-bold text-gray-700 text-lg mb-1 truncate group-hover:text-[#ff5c5c] transition-colors">{project.name}</h3>
              
              <div className="flex items-center text-[11px] font-bold text-gray-300 uppercase tracking-widest gap-2">
                <Clock size={12} strokeWidth={3} />
                <span>{new Date(project.updatedAt || Date.now()).toLocaleDateString()}</span>
              </div>
            </div>

            <button 
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('确定要删除这个项目吗？')) {
                  deleteProject(project.uuid);
                }
              }}
              className="absolute top-4 right-4 p-2.5 bg-white/90 backdrop-blur-sm text-gray-400 hover:text-red-500 hover:bg-white rounded-2xl opacity-0 group-hover:opacity-100 transition-all shadow-sm"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}

        {/* Empty Placeholder / Create Card */}
        <div 
          key="create-new-card"
          onClick={handleCreateNew}
          className="rounded-[32px] border-4 border-dashed border-pink-100 hover:border-[#ff5c5c]/30 transition-all cursor-pointer group flex flex-col items-center justify-center h-[280px] gap-4 hover:bg-white/50"
        >
          <div className="w-16 h-16 rounded-3xl bg-pink-50 flex items-center justify-center text-pink-300 group-hover:bg-[#ff5c5c] group-hover:text-white transition-all shadow-sm">
            <Plus size={32} strokeWidth={3} />
          </div>
          <span className="font-bold text-pink-200 group-hover:text-[#ff5c5c]">开始新创作</span>
        </div>
      </div>
    </div>
  );
};
